import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { restoreAthlete, type Api } from "api-client";
import { Icon } from "icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "ui";

import { ArchiveAthleteDialog } from "src/athlete/ArchiveAthleteDialog";
import { athleteLabelsCell } from "src/athlete/CoachAthleteList";
import type { Athlete, AthleteTableProps } from "src/athlete/CoachAthleteList";
import { useAuthStore } from "src/auth/AuthContext";
import {
  athleteFilterIds,
  createAthleteFilterConfig,
} from "src/components/athleteFilterConfig";
import { AthleteLimitBanner } from "src/components/AthleteLimitBanner";
import { AthleteLimitDialog } from "src/components/AthleteLimitDialog";
import { AthleteProductsCell } from "src/components/AthleteProductsCell";
import { CreateAthleteFilterDialog } from "src/components/CreateAthleteFilterDialog";
import { DataTable } from "src/components/DataTable";
import { type DataTableFeatures } from "src/components/dataTableFeatures";
import {
  type FilterAction,
  type SelectedFilters,
} from "src/components/filterConfig";
import { InviteAthleteToolbar } from "src/components/InviteAthleteToolbar";
import { LastWorkoutDateCell } from "src/components/LastWorkoutDateCell";
import { PartnershipCell } from "src/components/PartnershipCell";
import {
  useTableSearch,
  useTableSearchColumnFilters,
} from "src/components/TableSearchContext";
import { useLabelsRepository } from "src/contexts/LabelsRepositoryContext";
import { getAthleteLimitFromError } from "src/helpers/athleteLimitError";
import { useClipboard } from "src/hooks/useClipboard";
import { AssignEmailToAthleteDialog } from "src/invitations/AssignEmailToAthleteDialog";

function parseLastWorkoutRange(value: string) {
  const [gte, lte] = value.split(":").map(Number);

  if (gte === undefined || lte === undefined) {
    throw new Error("Invalid last workout range");
  }

  return { gte, lte };
}

export function AthletesTable(props: AthleteTableProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const clipboard = useClipboard();
  const navigate = useNavigate();
  const tableSearch = useTableSearch();
  const authStore = useAuthStore();
  const athleteFiltersEnabled =
    authStore.authenticatedData.entitlements.canAccessAthleteFilters;
  const labelsRepository = useLabelsRepository();
  const labelsQuery = useQuery(labelsRepository.listOptions);
  const products = props.athleteList.products;
  const [archiveDialogAthlete, setArchiveDialogAthlete] =
    useState<Athlete | null>(null);
  const [athleteLimitReached, setAthleteLimitReached] = useState<number | null>(
    null,
  );
  const [createFilterOpen, setCreateFilterOpen] = useState(false);

  const restoreAthleteMutation = useMutation({
    mutationFn: (athleteId: string) => {
      return restoreAthlete({ path: { athleteId } });
    },
    onSuccess: () => {
      props.onMutated();
    },
    onError: (error) => {
      const athleteLimit = getAthleteLimitFromError(error);

      if (athleteLimit !== null) {
        setAthleteLimitReached(athleteLimit);
        return;
      }

      toast.toast({
        title: t("athletesPage.restoreError.title"),
        description: t("athletesPage.restoreError.description"),
        variant: "destructive",
      });
    },
  });

  const { columnFilters, onColumnFiltersChange } = useTableSearchColumnFilters([
    { columnId: "labels" },
    { columnId: "partnership", searchKey: "partnershipTypes" },
    { columnId: "lastWorkoutDate" },
    { columnId: "products" },
  ]);

  function selectedValues(columnId: string) {
    return (
      (columnFilters.find((filter) => {
        return filter.id === columnId;
      })?.value as string[] | undefined) ?? []
    );
  }

  const selectedLabelIds = selectedValues("labels");
  const selectedPartnershipTypes = selectedValues("partnership");
  const selectedLastWorkoutDate = selectedValues("lastWorkoutDate");
  const selectedProducts = selectedValues("products");
  const savedFilterActive =
    athleteFiltersEnabled &&
    typeof tableSearch.extraFilters.filterId === "string";
  const filterConfig = createAthleteFilterConfig({
    t,
    labels: labelsQuery.data ?? [],
    products,
    includeLastWorkoutDate: athleteFiltersEnabled,
  });
  const selectedFilters: SelectedFilters = {
    [athleteFilterIds.labels]: selectedLabelIds,
    [athleteFilterIds.partnershipTypes]: selectedPartnershipTypes,
    [athleteFilterIds.lastWorkoutDate]: selectedLastWorkoutDate,
    [athleteFilterIds.products]: selectedProducts,
  };

  function toggleFilterValue(columnId: string, value: string) {
    const currentValues = selectedValues(columnId);
    const nextValues = currentValues.includes(value)
      ? currentValues.filter((currentValue) => {
          return currentValue !== value;
        })
      : [...currentValues, value];

    onColumnFiltersChange([
      ...columnFilters.filter((filter) => {
        return filter.id !== columnId;
      }),
      ...(nextValues.length > 0 ? [{ id: columnId, value: nextValues }] : []),
    ]);
  }

  function clearFilters() {
    onColumnFiltersChange([]);
  }

  function handleFilterAction(action: FilterAction) {
    if (action.type === "filters-cleared") {
      clearFilters();
      return;
    }

    if (action.filter === athleteFilterIds.labels) {
      toggleFilterValue("labels", action.value);
      return;
    }

    if (action.filter === athleteFilterIds.partnershipTypes) {
      toggleFilterValue("partnership", action.value);
      return;
    }

    if (action.filter === athleteFilterIds.products) {
      toggleFilterValue("products", action.value);
      return;
    }

    if (action.filter === athleteFilterIds.lastWorkoutDate) {
      onColumnFiltersChange([
        ...columnFilters.filter((filter) => {
          return filter.id !== "lastWorkoutDate";
        }),
        ...(action.type === "filter-selected"
          ? [{ id: "lastWorkoutDate", value: [action.value] }]
          : []),
      ]);
      return;
    }

    throw new Error(`Unknown athlete filter: ${action.filter}`);
  }

  const lastWorkoutRange = selectedLastWorkoutDate[0]
    ? parseLastWorkoutRange(selectedLastWorkoutDate[0])
    : null;
  const hasPartnershipCriteria =
    selectedLabelIds.length > 0 || selectedPartnershipTypes.length > 0;
  const criteria: NonNullable<Api.CreateAthleteFilter.Input>["criteria"] = {
    ...(hasPartnershipCriteria
      ? {
          partnership: {
            ...(selectedLabelIds.length > 0
              ? { labelId: { in: selectedLabelIds } }
              : {}),
            ...(selectedPartnershipTypes.length > 0
              ? {
                  type: {
                    in: selectedPartnershipTypes as (
                      | "ONLINE"
                      | "IN_PERSON"
                      | "HYBRID"
                    )[],
                  },
                }
              : {}),
          },
        }
      : {}),
    ...(lastWorkoutRange
      ? {
          calendarItem: {
            lastWorkoutDate: { daysFromToday: lastWorkoutRange },
          },
        }
      : {}),
  };
  const hasSavableFilters = hasPartnershipCriteria || !!lastWorkoutRange;

  function handleFilterCreated(filterId: string) {
    navigate({ to: "/athletes", search: { filterId } });
    props.onMutated();
  }

  const columns: ColumnDef<DataTableFeatures, Athlete>[] = [
    {
      id: "name",
      header: t("athletesPage.name"),
      cell: ({ row }) => {
        return <span>{row.original.user.name || "-"}</span>;
      },
    },
    {
      id: "email",
      header: t("athletesPage.email"),
      cell: ({ row }) => {
        if (row.original.user.email) {
          return (
            <span className="block max-w-full truncate">
              {row.original.user.email}
            </span>
          );
        }

        if (props.tab === "active" && row.original.user.status === "DRAFT") {
          return (
            <AssignEmailToAthleteDialog
              athleteId={row.original.id}
              onInviteSuccess={() => {
                props.onMutated();
              }}
            />
          );
        }

        return null;
      },
    },
    {
      id: "partnership",
      header: t("athletesPage.partnership"),
      cell: ({ row }) => {
        return (
          <PartnershipCell
            type={row.original.partnershipType}
            status={row.original.status}
          />
        );
      },
    },
    {
      id: "lastWorkoutDate",
      header: t("athletesPage.lastWorkoutDate"),
      cell: ({ row }) => {
        return (
          <LastWorkoutDateCell
            active={row.original.status === "ACTIVE"}
            date={row.original.lastWorkoutDate}
          />
        );
      },
    },
    {
      id: "products",
      header: t("athletesPage.products"),
      cell: ({ row }) => {
        return <AthleteProductsCell products={row.original.products} />;
      },
    },
    {
      id: "labels",
      header: t("athletesPage.labels"),
      size: 160,
      cell: athleteLabelsCell,
    },
  ];

  return (
    <>
      <AthleteLimitBanner
        athleteCount={props.athleteList.athleteCount}
        athleteLimit={props.athleteList.athleteLimit}
      />
      <DataTable
        columns={columns}
        data={props.athleteList.currentPage}
        getRowId={(row) => {
          return row.id;
        }}
        rowHref={
          props.tab === "active"
            ? (row) => {
                return `/athletes/${row.original.id}/profile`;
              }
            : undefined
        }
        searchPlaceholder={t("athletesPage.searchPlaceholder")}
        globalFilter={tableSearch.q}
        onGlobalFilterChange={tableSearch.setSearch}
        filters={{
          config: filterConfig,
          selectedFilters,
          onAction: handleFilterAction,
          saveAction:
            athleteFiltersEnabled && hasSavableFilters && !savedFilterActive
              ? {
                  label: t("athletesPage.filters.save"),
                  onSelect: () => {
                    setCreateFilterOpen(true);
                  },
                }
              : undefined,
        }}
        externalFilterState={{
          hasActiveFilters: savedFilterActive,
          hideClearAction: savedFilterActive,
          clearFilters: () => {
            tableSearch.setExtraFilter("filterId", undefined);
          },
        }}
        toolbarTrailing={
          <InviteAthleteToolbar
            athleteCount={props.athleteList.athleteCount}
            visibleAthleteCount={props.athleteList.pageInfo.count}
            athleteLimit={props.athleteList.athleteLimit}
            onMutated={props.onMutated}
          />
        }
        emptyState={{
          icon: "tabler-users",
          title: t("athletesPage.noAthletes.title"),
          description: t("athletesPage.noAthletes.description"),
        }}
        manualFiltering
        manualPagination
        rowCount={props.athleteList.pageInfo.count}
        pagination={tableSearch.pagination}
        onPaginationChange={tableSearch.onPaginationChange}
        rowActions={(row) => {
          if (props.tab === "archived") {
            return [
              {
                id: "restore",
                label: t("athletesPage.restore"),
                icon: <Icon name="tabler-archive-off" />,
                onSelect: () => {
                  restoreAthleteMutation.mutate(row.original.id);
                },
              },
            ];
          }

          const invitationId = row.original.invitationId;

          return [
            ...(row.original.status === "PENDING" && invitationId
              ? [
                  {
                    id: "copyInvitationLink",
                    label: t("athletesPage.copyInvitationLink"),
                    icon: <Icon name="tabler-copy" />,
                    onSelect: () => {
                      void clipboard.copy(
                        `${window.location.origin}/invitations/${invitationId}`,
                      );
                      toast.toast({
                        title: t("athletesPage.copyInvitationLinkSuccess"),
                      });
                    },
                  },
                ]
              : []),
            {
              id: "archive",
              label: t("athletesPage.archive"),
              icon: <Icon name="tabler-archive" />,
              destructive: true,
              onSelect: () => {
                setArchiveDialogAthlete(row.original);
              },
            },
          ];
        }}
      />
      {archiveDialogAthlete && (
        <ArchiveAthleteDialog
          athlete={archiveDialogAthlete}
          open={!!archiveDialogAthlete}
          onOpenChange={(open) => {
            if (!open) setArchiveDialogAthlete(null);
          }}
          onSuccess={() => {
            props.onMutated();
          }}
        />
      )}
      <CreateAthleteFilterDialog
        open={createFilterOpen}
        criteria={criteria}
        onOpenChange={setCreateFilterOpen}
        onCreated={handleFilterCreated}
      />
      {athleteLimitReached !== null && (
        <AthleteLimitDialog
          athleteLimit={athleteLimitReached}
          open
          onOpenChange={(open) => {
            if (!open) {
              setAthleteLimitReached(null);
            }
          }}
        />
      )}
    </>
  );
}
